import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LeaveStatement from './LeaveStatement'; // Reusing the statement component

// --- Sub-component for the "Team" tab ---
function TeamStatementView({ userProfile, appId }) {
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUserProfile, setSelectedUserProfile] = useState(null);
    const [selectedUserLeaves, setSelectedUserLeaves] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // --- STATE FOR SEARCHABLE INPUT ---
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Fetch the list of employees for the dropdown
    useEffect(() => {
        let q;
        const usersRef = collection(db, `/artifacts/${appId}/users`);
        // Admins/HR see everyone, Managers see only their direct reports
        if (userProfile.role === 'Admin' || userProfile.role === 'HR') {
            q = query(usersRef, where("status", "==", "Approved"));
        } else {
            q = query(usersRef, where("managerId", "==", userProfile.uid), where("status", "==", "Approved"));
        }

        const unsub = onSnapshot(q, (snapshot) => {
            const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeamMembers(members);
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId, userProfile]);

    // Fetch leave data for the selected employee
    useEffect(() => {
        if (!selectedUserId) {
            setSelectedUserProfile(null);
            setSelectedUserLeaves([]);
            return;
        }

        const selectedUser = teamMembers.find(m => m.id === selectedUserId);
        setSelectedUserProfile(selectedUser);

        const requestsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/leaveRequests`),
            where("requesterId", "==", selectedUserId)
        );
        const unsub = onSnapshot(requestsQuery, (snapshot) => {
            setSelectedUserLeaves(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();

    }, [selectedUserId, teamMembers, appId]);

    // --- DERIVED STATE FOR FILTERING ---
    const filteredTeamMembers = useMemo(() => {
        if (!searchTerm) {
            return teamMembers; // Show all members if search is empty
        }
        return teamMembers.filter(member =>
            member.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, teamMembers]);

    // --- HANDLERS FOR SEARCHABLE INPUT ---
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setIsDropdownOpen(true);
        // If user clears the search input, deselect the current user
        if (value === '') {
            setSelectedUserId('');
        }
    };

    const handleSelectMember = (member) => {
        setSelectedUserId(member.id);
        setSearchTerm(member.name); // Display selected member's name in the input
        setIsDropdownOpen(false);
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <div className="mb-6 max-w-sm relative">
                <label htmlFor="employee-search" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Employee
                </label>
                <input
                    id="employee-search"
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    // Use a short timeout on blur to allow click events on dropdown items to register
                    onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                    placeholder="Type to search for a team member..."
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    autoComplete="off"
                />
                {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredTeamMembers.length > 0 ? (
                            <ul className="py-1">
                                {filteredTeamMembers.map(member => (
                                    <li
                                        key={member.id}
                                        // Use onMouseDown to fire before onBlur, preventing the dropdown from closing before selection
                                        onMouseDown={() => handleSelectMember(member)}
                                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                    >
                                        {member.name}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="px-4 py-2 text-sm text-gray-500">No members found.</p>
                        )}
                    </div>
                )}
            </div>

            {selectedUserProfile && (
                <div>
                    <LeaveStatement allRequests={selectedUserLeaves} userProfile={selectedUserProfile} />
                </div>
            )}
        </div>
    );
}

// --- Sub-component for the "My Statement" tab ---
function MyStatementView({ userProfile, appId }) {
    const [myRequests, setMyRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const requestsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/leaveRequests`),
            where("requesterId", "==", userProfile.uid)
        );
        const unsub = onSnapshot(requestsQuery, (snapshot) => {
            setMyRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId, userProfile.uid]);

    if (isLoading) return <LoadingSpinner />;

    return <LeaveStatement allRequests={myRequests} userProfile={userProfile} />;
}


// --- Main component for the "Leave Statements" view ---
export default function LeaveStatements({ userProfile, appId }) {
    const [activeTab, setActiveTab] = useState('myStatement');

    return (
        <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold text-[#213368] mb-4">Leave Statements</h2>
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('myStatement')}
                        className={`${activeTab === 'myStatement' ? 'border-[#213368] text-[#213368]' : 'border-transparent text-gray-500 hover:text-gray-700'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    >
                        My Statement
                    </button>
                    {['Admin', 'HR', 'Manager'].includes(userProfile.role) && (
                        <button
                            onClick={() => setActiveTab('team')}
                            className={`${activeTab === 'team' ? 'border-[#213368] text-[#213368]' : 'border-transparent text-gray-500 hover:text-gray-700'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Team Statements
                        </button>
                    )}
                </nav>
            </div>
            <div className="pt-6">
                {activeTab === 'myStatement' && <MyStatementView userProfile={userProfile} appId={appId} />}
                {activeTab === 'team' && <TeamStatementView userProfile={userProfile} appId={appId} />}
            </div>
        </div>
    );
}

